import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { narApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const P   = '#6B3FA0';
const PD  = '#4A2B70';
const PL  = '#F3EEFA';
const PLM = '#E5D5F5';

const ANSWER_META = {
  met:           { label: 'Met',           bg: '#D1FAE5', color: '#065F46' },
  partially_met: { label: 'Partially Met', bg: '#FEF3C7', color: '#92400E' },
  not_met:       { label: 'Not Met',       bg: '#FEE2E2', color: '#991B1B' },
  na:            { label: 'N/A',           bg: '#F3F4F6', color: '#6B7280' },
};

function gradeColor(g) {
  if (!g || g === 'N/A') return '#6B7280';
  if (g.startsWith('A'))  return '#16a34a';
  if (g.startsWith('B'))  return P;
  if (g.startsWith('C'))  return '#ca8a04';
  if (g.startsWith('D'))  return '#f97316';
  return '#dc2626';
}

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—';

export default function NarReport() {
  const { narId }  = useParams();
  const navigate   = useNavigate();
  const { user }   = useAuth();

  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [downloading, setDown]    = useState('');
  const [genAI, setGenAI]         = useState(false);
  const [vcioEdit, setVcioEdit]   = useState('');
  const [savingVcio, setSavingVcio] = useState(false);
  const [filterAns, setFilterAns] = useState('all');

  useEffect(() => {
    narApi.getReportData(narId)
      .then(res => {
        setData(res.data);
        setVcioEdit(res.data.nar?.vcio_summary || '');
      })
      .catch(() => { toast.error('Failed to load report'); navigate('/nar/assessments'); })
      .finally(() => setLoading(false));
  }, [narId]);

  const handleDownload = async (type) => {
    setDown(type);
    try {
      const name = data?.nar?.name || narId;
      if (type === 'pdf')  await narApi.downloadPdf(narId, name);
      if (type === 'xlsx') await narApi.downloadXlsx(narId, name);
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    } finally {
      setDown('');
    }
  };

  const handleGenerateSummary = async () => {
    setGenAI(true);
    try {
      const res = await narApi.generateSummary(narId);
      setVcioEdit(res.data.summary || '');
      toast.success('AI summary generated — review and save.');
    } catch (e) {
      toast.error(e.response?.data?.error || 'AI generation failed');
    } finally {
      setGenAI(false);
    }
  };

  const handleSaveVcio = async () => {
    setSavingVcio(true);
    try {
      await narApi.updateNar(narId, { vcio_summary: vcioEdit });
      setData(d => ({ ...d, nar: { ...d.nar, vcio_summary: vcioEdit } }));
      toast.success('vCIO summary saved');
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSavingVcio(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '80px', color: '#6B7280' }}>Loading report…</div>;
  if (!data)   return null;

  const { nar, sections, score } = data;
  const gc = gradeColor(score.grade);
  const isAdmin = user?.role === 'admin';
  const dateStr = fmtDate(nar.submitted_at || nar.updated_at || nar.created_at);

  const btn = (bg, color, border) => ({
    padding: '9px 18px', backgroundColor: bg, color, border: border || 'none',
    borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
  });

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <button onClick={() => navigate('/nar/assessments')}
            style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: '13px', padding: 0, marginBottom: '8px' }}>
            ← Back to NAR List
          </button>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: PD }}>{nar.name}</h1>
          <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#6B7280' }}>
            {nar.client_name} · {nar.nar_type_name} · {dateStr}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button onClick={() => navigate(`/nar/assessments/${narId}/conduct`)} style={btn(PL, PD, `1px solid ${PLM}`)}>
            View / Edit Answers
          </button>
          <button onClick={() => handleDownload('xlsx')} disabled={!!downloading}
            style={{ ...btn('#D1FAE5', '#065F46', '1px solid #A7F3D0'), opacity: downloading === 'xlsx' ? 0.7 : 1 }}>
            {downloading === 'xlsx' ? 'Downloading…' : '⬇ Download XLSX'}
          </button>
          <button onClick={() => handleDownload('pdf')} disabled={!!downloading}
            style={{ ...btn(P, 'white'), opacity: downloading === 'pdf' ? 0.7 : 1 }}>
            {downloading === 'pdf' ? 'Generating PDF…' : '⬇ Download PDF'}
          </button>
        </div>
      </div>

      {/* Score summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: 'Overall Score', value: `${score.score}%`, color: gc, large: true },
          { label: 'Grade',         value: score.grade,       color: gc, large: true },
          { label: 'Points Achieved', value: `${score.achieved_points} / ${score.available_points}`, color: PD },
          { label: 'Questions Answered', value: `${score.answered_questions} / ${score.total_questions}`, color: PD },
        ].map(({ label, value, color, large }) => (
          <div key={label} style={{ backgroundColor: 'white', borderRadius: '8px', padding: '16px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)', textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '6px', fontWeight: '500' }}>{label}</div>
            <div style={{ fontSize: large ? '28px' : '20px', fontWeight: '800', color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Section scores */}
      {score.section_scores?.length > 0 && (
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)', marginBottom: '20px' }}>
          <h2 style={{ margin: '0 0 14px', fontSize: '16px', fontWeight: '700', color: PD }}>Section Scores</h2>
          {score.section_scores.map(s => {
            const c = s.score >= 80 ? '#16a34a' : s.score >= 60 ? '#ca8a04' : '#dc2626';
            return (
              <div key={s.section_id} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', color: '#374151', marginBottom: '4px', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</div>
                  <div style={{ height: '8px', backgroundColor: PLM, borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ width: `${s.score}%`, height: '100%', backgroundColor: c, borderRadius: '4px', transition: 'width 0.4s ease' }} />
                  </div>
                </div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: c, minWidth: '44px', textAlign: 'right' }}>{s.score}%</div>
              </div>
            );
          })}
        </div>
      )}

      {/* vCIO Summary */}
      {isAdmin && (
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: PD }}>vCIO Executive Summary</h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleGenerateSummary} disabled={genAI}
                style={{ ...btn(PL, PD, `1px solid ${PLM}`), opacity: genAI ? 0.7 : 1 }}>
                {genAI ? 'Generating…' : '✨ Generate AI Summary'}
              </button>
              <button onClick={handleSaveVcio} disabled={savingVcio}
                style={{ ...btn(P, 'white'), opacity: savingVcio ? 0.7 : 1 }}>
                {savingVcio ? 'Saving…' : 'Save Summary'}
              </button>
            </div>
          </div>
          <textarea
            value={vcioEdit}
            onChange={e => setVcioEdit(e.target.value)}
            rows={12}
            placeholder="Write or generate a vCIO executive summary here. It will appear in the PDF report."
            style={{ width: '100%', padding: '12px', border: `1px solid ${PLM}`, borderRadius: '6px', fontSize: '14px', lineHeight: '1.6', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
          />
          <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
            Use numbered lists (1. 2. 3.) for recommendations — they'll be formatted automatically in the PDF.
          </p>
        </div>
      )}

      {!isAdmin && nar.vcio_summary && (
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)', marginBottom: '20px' }}>
          <h2 style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: '700', color: PD }}>vCIO Executive Summary</h2>
          <div style={{ fontSize: '14px', color: '#374151', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>
            {nar.vcio_summary}
          </div>
        </div>
      )}

      {/* Assessment Detail */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: `2px solid ${PLM}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: PD }}>Assessment Detail</h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            {['all', 'not_met', 'partially_met', 'met', 'na'].map(f => {
              const meta = ANSWER_META[f];
              const active = filterAns === f;
              return (
                <button key={f} onClick={() => setFilterAns(f)}
                  style={{ padding: '5px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: active ? '700' : '500', cursor: 'pointer', border: `1px solid ${active ? (meta?.color || PD) : '#E5E7EB'}`, backgroundColor: active ? (meta?.bg || PL) : 'white', color: active ? (meta?.color || PD) : '#6B7280' }}>
                  {f === 'all' ? 'All' : meta.label}
                </button>
              );
            })}
          </div>
        </div>

        {sections.map(sec => {
          const visibleCats = (sec.categories || []).map(cat => {
            const visibleQs = (cat.questions || []).filter(q => {
              const ans = q.answer_data?.answer || null;
              return filterAns === 'all' || ans === filterAns || (filterAns === 'not_met' && !ans);
            });
            return visibleQs.length ? { ...cat, questions: visibleQs } : null;
          }).filter(Boolean);

          if (!visibleCats.length) return null;

          return (
            <div key={sec.id}>
              <div style={{ padding: '12px 20px', backgroundColor: PD, color: 'white', fontWeight: '700', fontSize: '14px' }}>
                {sec.name}
              </div>
              {visibleCats.map(cat => (
                <div key={cat.id}>
                  <div style={{ padding: '8px 20px', backgroundColor: '#F9FAFB', borderBottom: '1px solid #F3F4F6', fontSize: '13px', fontWeight: '600', color: PD }}>
                    {cat.name}
                  </div>
                  {cat.questions.map((q, qi) => {
                    const ans = q.answer_data?.answer;
                    const meta = ANSWER_META[ans] || { label: 'Unanswered', bg: '#FEF3C7', color: '#92400E' };
                    return (
                      <div key={q.id} style={{ display: 'flex', alignItems: 'flex-start', padding: '12px 20px', borderBottom: '1px solid #F9FAFB', backgroundColor: qi % 2 === 0 ? 'white' : '#FDFCFF' }}>
                        <div style={{ flex: 1, fontSize: '13px', color: '#374151', lineHeight: '1.5', paddingRight: '16px' }}>
                          {q.question_text}
                          {q.answer_data?.comments && (
                            <div style={{ marginTop: '4px', fontSize: '12px', color: '#6B7280', fontStyle: 'italic' }}>
                              {q.answer_data.comments}
                            </div>
                          )}
                          {q.answer_data?.vcio_comments && (
                            <div style={{ marginTop: '3px', fontSize: '12px', color: P, fontStyle: 'italic' }}>
                              vCIO: {q.answer_data.vcio_comments}
                            </div>
                          )}
                        </div>
                        <span style={{ flexShrink: 0, padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '700', backgroundColor: meta.bg, color: meta.color }}>
                          {meta.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
